import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ArrowRight, Grid3X3, Image as ImageIcon, Layers3 } from "lucide-react";
import { GetCategories } from "../../common/CommonApi";

const HomeApp = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const resp = await GetCategories();
      setCategories(resp.data.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  function openCategory(id) {
    navigate(`/home/category?catid=${id}`);
  }

  return (
    <Container>
      <PageHeader>
        <div>
          <Title>Home Sections</Title>
          <Subtitle>Choose a category to manage the sections shown in the mobile home experience.</Subtitle>
        </div>
      </PageHeader>

      <SummaryGrid>
        <SummaryCard>
          <SummaryIcon>
            <Grid3X3 size={20} />
          </SummaryIcon>
          <div>
            <SummaryNumber>{categories.length}</SummaryNumber>
            <SummaryLabel>Categories</SummaryLabel>
          </div>
        </SummaryCard>
      </SummaryGrid>

      {loading && (
        <Loader>
          <img src="/assets/loading.gif" alt="Loading categories" />
        </Loader>
      )}

      {!loading && categories.length === 0 && (
        <EmptyState>
          <Layers3 size={34} />
          <strong>No categories found</strong>
          <span>Create categories first to configure home sections.</span>
        </EmptyState>
      )}

      {!loading && categories.length > 0 && (
        <CategoryGrid>
          {categories.map((cat) => (
            <CategoryCard key={cat._id} type="button" onClick={() => openCategory(cat._id)}>
              <CategoryMedia>
                {cat.categoryImage ? (
                  <img src={cat.categoryImage} alt={cat.categoryName || "Category"} />
                ) : (
                  <ImageIcon size={26} />
                )}
              </CategoryMedia>
              <CategoryContent>
                <CategoryName>{cat.categoryName}</CategoryName>
                <CategoryDescription>{cat.description || "Manage sections for this category."}</CategoryDescription>
              </CategoryContent>
              <CardAction>
                Manage sections
                <ArrowRight size={15} />
              </CardAction>
            </CategoryCard>
          ))}
        </CategoryGrid>
      )}
    </Container>
  );
};

export default HomeApp;

const Container = styled.div`
  min-height: 100vh;
  padding: 28px;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 0;
    min-height: auto;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
`;

const SummaryIcon = styled.div`
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #ecfeff;
  color: #0f766e;
`;

const SummaryNumber = styled.div`
  color: #0f172a;
  font-size: 24px;
  font-weight: 900;
`;

const SummaryLabel = styled.div`
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.button`
  min-height: 260px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  text-align: left;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #30d5c8;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
  }
`;

const CategoryMedia = styled.div`
  height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #94a3b8;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 12px;
  }
`;

const CategoryContent = styled.div`
  flex: 1;
  padding: 16px;
`;

const CategoryName = styled.div`
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
`;

const CategoryDescription = styled.div`
  display: -webkit-box;
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const CardAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 16px;
  border-top: 1px solid #e2e8f0;
  color: #0f766e;
  font-size: 13px;
  font-weight: 900;
`;

const Loader = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 78px;
    height: 78px;
    object-fit: contain;
  }
`;

const EmptyState = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #64748b;

  strong {
    color: #0f172a;
    font-size: 16px;
  }
`;
